/* ============================================================
   ARDAH — ANIMATION ENGINE v1.0
   Bloom · Parallax · Cursor · Word Reveal · Warm Hover · Page Curtain
   Kashmir · Palestine · Syria · Libya · Iraq · Iran
   ============================================================ */

(function () {
  'use strict';

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = () => !window.matchMedia('(hover: hover) and (pointer: fine)').matches;


  /* ─── 1. BLOOM OBSERVER ────────────────────────────────── */

  class ArdahBloomObserver {
    constructor() {
      this.observer = null;
      this.init();
    }

    init() {
      if (!('IntersectionObserver' in window)) {
        this.revealAll();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            el.classList.add('ardah-bloom--visible');
            this.observer.unobserve(el);
          });
        },
        { rootMargin: '0px 0px -48px 0px', threshold: 0.06 }
      );

      this.observeAll();
    }

    observeAll(root = document) {
      root.querySelectorAll('.ardah-bloom').forEach(el => {
        this.observer.observe(el);
      });

      /* Stagger groups: re-index children within each parent */
      const parents = new Set();
      root.querySelectorAll('.ardah-stagger-child').forEach(el => {
        parents.add(el.parentElement);
      });
      parents.forEach(parent => {
        if (!parent) return;
        const children = Array.from(parent.querySelectorAll('.ardah-stagger-child'));
        children.forEach((child, i) => {
          child.style.setProperty('--ardah-stagger-index', i);
          this.observer.observe(child);
        });
      });
    }

    revealAll() {
      document.querySelectorAll('.ardah-bloom, .ardah-stagger-child').forEach(el => {
        el.classList.add('ardah-bloom--visible');
      });
    }

    /* Called by Shopify design mode on section reload */
    refresh(root) {
      if (this.observer) this.observeAll(root);
    }
  }


  /* ─── 2. PARALLAX ENGINE ───────────────────────────────── */

  class ArdahParallax {
    constructor() {
      this.items = [];
      this.rafId = null;
      this.ticking = false;

      const RATES = { slow: 0.12, mid: 0.32, fast: 0.56 };

      document.querySelectorAll('[data-ardah-parallax]').forEach(el => {
        const rate = RATES[el.dataset.ardahParallax] || 0.2;
        this.items.push({ el, rate });
      });

      if (!this.items.length) return;

      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.update();
    }

    onScroll() {
      if (!this.ticking) {
        this.rafId = requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }

    update() {
      const vH = window.innerHeight;
      this.items.forEach(({ el, rate }) => {
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2 - vH / 2;
        const offset = -(center * rate).toFixed(2);
        el.style.transform = `translate3d(0, ${offset}px, 0)`;
      });
    }

    destroy() {
      cancelAnimationFrame(this.rafId);
      window.removeEventListener('scroll', this.onScroll);
    }
  }


  /* ─── 3. CURSOR GLOW ───────────────────────────────────── */

  class ArdahCursor {
    constructor() {
      this.el = document.getElementById('ardah-cursor-glow');
      if (!this.el || isTouch()) {
        if (this.el) this.el.style.display = 'none';
        return;
      }

      this.curX = 0;
      this.curY = 0;
      this.rafId = null;
      this.init();
    }

    init() {
      document.addEventListener('mousemove', (e) => {
        this.curX = e.clientX;
        this.curY = e.clientY;
        if (!this.rafId) {
          this.rafId = requestAnimationFrame(() => {
            this.el.style.left = this.curX + 'px';
            this.el.style.top  = this.curY + 'px';
            this.rafId = null;
          });
        }
      }, { passive: true });

      document.addEventListener('mouseleave', () => {
        this.el.style.opacity = '0';
      });
      document.addEventListener('mouseenter', () => {
        this.el.style.opacity = '1';
      });

      /* Scale up on interactive elements */
      document.addEventListener('mouseover', (e) => {
        if (e.target.closest('a, button, [role="button"], .ardah-warm-hover')) {
          this.el.style.width  = '110px';
          this.el.style.height = '110px';
          this.el.style.opacity = '0.75';
        } else {
          this.el.style.width  = '';
          this.el.style.height = '';
          this.el.style.opacity = '1';
        }
      }, { passive: true });
    }
  }


  /* ─── 4. WORD REVEAL ───────────────────────────────────── */

  class ArdahWordReveal {
    constructor() {
      this.init();
    }

    init() {
      document.querySelectorAll('.ardah-word-reveal').forEach(el => {
        if (el.dataset.ardahWordSplit) return; /* already processed */
        this.split(el);
        el.dataset.ardahWordSplit = '1';
      });
    }

    split(el) {
      /* Preserve inner HTML tags — only split top-level text nodes */
      const nodes = Array.from(el.childNodes);
      el.innerHTML = '';

      nodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          const words = node.textContent.split(/(\s+)/);
          words.forEach((chunk, i) => {
            if (/^\s+$/.test(chunk)) {
              el.appendChild(document.createTextNode(chunk));
            } else if (chunk) {
              const wordIndex = el.querySelectorAll('.ardah-word').length;
              const span = document.createElement('span');
              span.className = 'ardah-word';
              span.style.setProperty('--ardah-word-index', wordIndex);
              span.innerHTML = `<span class="ardah-word-inner">${chunk}</span>`;
              el.appendChild(span);
            }
          });
        } else {
          /* Wrap element nodes as-is */
          el.appendChild(node.cloneNode(true));
        }
      });
    }
  }


  /* ─── 5. WARM HOVER LIGHT ──────────────────────────────── */

  class ArdahWarmHover {
    constructor() {
      this.init();
    }

    init() {
      document.querySelectorAll('.ardah-warm-hover').forEach(el => {
        el.addEventListener('mousemove', (e) => {
          const rect = el.getBoundingClientRect();
          const x = (((e.clientX - rect.left) / rect.width)  * 100).toFixed(1);
          const y = (((e.clientY - rect.top)  / rect.height) * 100).toFixed(1);
          el.style.setProperty('--ardah-mouse-x', x + '%');
          el.style.setProperty('--ardah-mouse-y', y + '%');
        }, { passive: true });
      }, { passive: true });
    }
  }


  /* ─── 6. PAGE CURTAIN ──────────────────────────────────── */

  class ArdahPageCurtain {
    constructor() {
      this.curtain = document.getElementById('ardah-page-curtain');
      if (!this.curtain) return;
      this.pending = false;
      this.init();
    }

    init() {
      /* Entrance: open curtain on load */
      requestAnimationFrame(() => {
        this.curtain.classList.add('ardah-curtain--opening');
        setTimeout(() => {
          this.curtain.classList.remove('ardah-curtain--closing', 'ardah-curtain--opening');
        }, 580);
      });

      /* Exit: catch clicks on same-origin links */
      document.addEventListener('click', (e) => {
        if (this.pending) return;
        const link = e.target.closest('a[href]');
        if (!link) return;

        const href = link.getAttribute('href');
        if (!href) return;

        /* Skip: hash, external, new tab, download, special keys */
        if (
          href.startsWith('#') ||
          href.startsWith('mailto:') ||
          href.startsWith('tel:') ||
          link.target === '_blank' ||
          link.hasAttribute('download') ||
          e.metaKey || e.ctrlKey || e.shiftKey || e.altKey
        ) return;

        let isSameOrigin = false;
        try {
          const url = new URL(href, window.location.href);
          if (url.origin !== window.location.origin) return;
          if (url.href === window.location.href) return;
          isSameOrigin = true;
        } catch (_) {
          return;
        }

        if (!isSameOrigin) return;

        e.preventDefault();
        this.pending = true;

        this.curtain.classList.remove('ardah-curtain--opening');
        void this.curtain.offsetWidth; /* force reflow */
        this.curtain.classList.add('ardah-curtain--closing');

        setTimeout(() => {
          window.location.href = href;
        }, 540);
      });
    }
  }


  /* ─── 7. SHOPIFY DESIGN MODE SUPPORT ───────────────────── */

  function refreshForDesignMode(root) {
    if (window.__ardahBloom) {
      window.__ardahBloom.refresh(root);
    }
    /* Re-split any new word-reveal elements */
    if (window.__ardahWordReveal) {
      root.querySelectorAll('.ardah-word-reveal:not([data-ardah-word-split])').forEach(el => {
        window.__ardahWordReveal.split(el);
        el.dataset.ardahWordSplit = '1';
      });
    }
  }


  /* ─── 8. INITIALISATION ────────────────────────────────── */

  document.addEventListener('DOMContentLoaded', () => {

    /* Activate CSS animation system */
    document.body.classList.add('ardah-system-ready');

    if (prefersReduced) {
      /* Skip all motion, just reveal everything */
      document.querySelectorAll(
        '.ardah-bloom, .ardah-stagger-child, .ardah-word-reveal'
      ).forEach(el => el.classList.add('ardah-bloom--visible'));
      return;
    }

    /* Boot all modules */
    window.__ardahBloom      = new ArdahBloomObserver();
    window.__ardahWordReveal = new ArdahWordReveal();
    new ArdahWarmHover();
    new ArdahCursor();
    new ArdahPageCurtain();

    if (!isTouch()) {
      new ArdahParallax();
    }
  });

  /* Shopify section lifecycle */
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      refreshForDesignMode(e.target);
    });
    document.addEventListener('shopify:section:reorder', () => {
      refreshForDesignMode(document);
    });
  }

})();
